import { useEffect, useMemo, useState } from "react";
import type { PublicSellerBrandingView, PurchasablePlanView } from "@keydials/api-client";
import { api, getAccessToken, useAuth } from "../auth/AuthContext";
import { SellerBrand, priceLabel, sellerBrandStyle, usePartnerLightTheme } from "../pages/BuyPlans";
import { DiscountCodeField } from "./DiscountCodeField";
import { endedCopy, keptCopy } from "./trialEndedCopy";
import { pickReturnPlan } from "./trialEndedPlans";
import { collectMyData, myDataFileName } from "./myDataExport";

/**
 * What somebody sees when their access has ended (KEY-431, KEY-912): their card-free trial ran out, or — since
 * KEY-911 locks a cancellation out the same way — their subscription was cancelled. Worded by `endedReason`
 * (/api/me's, or the 402's code when the gate fired mid-session); the trial's wording is unchanged.
 *
 * The whole design of this screen is one sentence: <b>nothing has been deleted</b>. A trial that took no
 * card ends by simply stopping, and the person on the other side of it did nothing wrong — they signed up
 * at a stand, used the thing for a fortnight, and have now been shut out of work they built. So the
 * reassurance comes first and the price list second, rather than the other way round. Once data retention is
 * switched on (KEY-667) that reassurance gains an end date, and the sentence says so — see `keptCopy`.
 *
 * It is deliberately a dead end with exactly two ways off it: subscribe, or sign out. Offering a link back
 * into the app would only produce a 402, and a screen that appears to let you carry on and then refuses is
 * worse than one that is honest about where you are. Downloading their data (KEY-912) is not a way back in — it
 * reads only the export routes the gate leaves open — but "your data is safe" is a promise better kept by
 * letting them take a copy.
 *
 * The server refuses the dials regardless of what this component does — see ITrialExpiryGate. This exists
 * so the refusal is explained rather than experienced as a broken app.
 *
 * The shop it shows is the one they bought from (KEY-634): the rung their subscription names, and that
 * seller's other rungs beside it, with their branding. Asking for the whole catalogue instead showed a
 * partner's customer every seller's price list with Key Dials' first plan pre-selected — and left out an
 * unlisted rung altogether, which is exactly what a link-only sale is.
 */
export function AccessEnded() {
  const { me, logout, endedReason } = useAuth();
  const [plans, setPlans] = useState<PurchasablePlanView[] | null>(null);
  const [chosen, setChosen] = useState<string | null>(null);
  const [seller, setSeller] = useState<PublicSellerBrandingView | null | undefined>(undefined);
  /** A discount code (KEY-642) — the trial emails link here with `?code=` already on (KEY-647). */
  const [discountCode, setDiscountCode] = useState<string | null>(null);
  /** Whose family pack they were on, when that is why they are here (KEY-651); null for an ordinary trial. */
  const [sponsoredBy, setSponsoredBy] = useState<string | null>(null);
  /** Whether the lapsed-data countdown applies (KEY-667), and the date it has emailed them, if any. */
  const [retention, setRetention] = useState<{ on: boolean; deletionAtUtc: string | null }>({ on: false, deletionAtUtc: null });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /** KEY-912: the data download — idle, running, or what it had to say when it finished. */
  const [download, setDownload] = useState<{ busy: boolean; note: string | null }>({ busy: false, note: null });

  const organisationId = me?.organisationId ?? null;

  useEffect(() => {
    let cancelled = false;

    (async () => {
      // The rung they were on. Their subscription reads on billing.read, which an Organisation
      // Administrator holds, and it is not behind the trial gate. If it cannot be read the shop is the
      // whole catalogue, as before: better a shelf than a blank page.
      let currentPlanId: string | null = null;
      let sponsor: string | null = null;
      let kept = { on: false, deletionAtUtc: null as string | null };
      const token = getAccessToken();
      if (token && organisationId) {
        try {
          const subscription = await api.getOrgSubscription(token, organisationId);
          currentPlanId = subscription?.planId ?? null;
          sponsor = subscription?.sponsoredBy ?? null;
          kept = {
            on: subscription?.dataRetentionOn ?? false,
            deletionAtUtc: subscription?.dataDeletionScheduledAtUtc ?? null,
          };
        } catch {
          currentPlanId = null;
        }
      }
      if (!cancelled) {
        setSponsoredBy(sponsor);
        setRetention(kept);
      }

      // The server scopes the list to that rung's seller and includes the rung itself even when it is
      // kept off the website — a stale or unsellable id falls through to everything, by design.
      const list = await api.listPurchasablePlans(currentPlanId);
      if (cancelled) return;
      setPlans(list);
      setChosen(pickReturnPlan(list, currentPlanId, sponsor !== null));

      if (!currentPlanId) {
        setSeller(null);
        return;
      }
      // Branding is a nicety: if it can't be read, the page simply wears ours.
      const branding = await api.getPlanSeller(currentPlanId).catch(() => null);
      if (!cancelled) setSeller(branding);
    })().catch(() => {
      // Not fatal, and not worth an error box. The message above is the point of the page; if the price
      // list can't be reached they can still sign out and come back, or reply to the email.
      if (!cancelled) {
        setPlans([]);
        setSeller(null);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [organisationId]);

  usePartnerLightTheme(seller);

  const selected = useMemo(() => plans?.find((p) => p.id === chosen) ?? null, [plans, chosen]);
  const reason = endedReason ?? "trial";
  const kept = keptCopy(retention.on, retention.deletionAtUtc, me?.organisationName, reason);
  const copy = endedCopy(reason, sponsoredBy);

  const downloadMyData = async () => {
    const token = getAccessToken();
    if (!token) return;
    setDownload({ busy: true, note: null });
    try {
      const now = new Date();
      const file = await collectMyData(
        {
          listInstances: () => api.listInstances(token),
          exportInstance: (id) => api.exportInstance(token, id),
          exportInstanceItems: (id) => api.exportInstanceItems(token, id),
        },
        now,
      );
      const blob = new Blob([JSON.stringify(file, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = myDataFileName(me?.organisationName, now);
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      setDownload({
        busy: false,
        note:
          file.dials.length === 0
            ? "There were no dials to download."
            : file.skipped.length > 0
              ? `Downloaded ${file.dials.length} dial${file.dials.length === 1 ? "" : "s"}. ${file.skipped.length} couldn't be included: ${file.skipped.map((s) => s.name).join(", ")}.`
              : null,
      });
    } catch {
      setDownload({ busy: false, note: "Your data couldn't be downloaded just now. Try again in a moment." });
    }
  };

  const subscribe = async () => {
    const token = getAccessToken();
    if (!selected || !me?.organisationId || !token) return;
    setBusy(true);
    setError(null);
    try {
      // Named organisation, so the purchase reattaches to the account they already have. The anonymous
      // buy page would create a SECOND organisation for the same address, and they would sign back in to
      // an empty one beside the full one we just promised them was kept.
      const { url } = await api.reactivateSubscription(token, me.organisationId, {
        planId: selected.id,
        email: me.email,
        discountCode,
      });
      window.location.assign(url);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Couldn't start your subscription.");
      setBusy(false);
    }
  };

  return (
    <main className="buy-page" style={sellerBrandStyle(seller)}>
      <SellerBrand seller={seller} />
      <header className="buy-header">
        {/* A relative on somebody's family pack (KEY-651) lands here when their place ends — the pack ended,
            or the buyer took them off it. Same screen, same way back; only who paid until now differs. */}
        <h1>{copy.heading}</h1>
        {/* KEY-667: "nothing has been deleted" only while data retention is off for them — see trialEndedCopy. */}
        <p className="trial-kept">
          <strong>{kept.lead}</strong> {kept.rest}
        </p>
        {/* KEY-912: a cancelled customer did pay, so the trial's "we never took a card" line is not said to them. */}
        {copy.paymentNote && <p className="buy-muted">{copy.paymentNote}</p>}
      </header>

      {plans && plans.length > 0 && (
        <section className="buy-family">
          <h2>Choose a plan</h2>
          <div className="buy-tiers">
            {plans.map((plan) => {
              const isChosen = plan.id === chosen;
              return (
                <button
                  key={plan.id}
                  type="button"
                  className={`buy-tier${isChosen ? " is-chosen" : ""}`}
                  aria-pressed={isChosen}
                  onClick={() => setChosen(plan.id)}
                >
                  <span className="buy-tier-name">
                    {plan.familyLabel} {plan.displayName ?? plan.tierName}
                  </span>
                  <span className="buy-tier-price">{priceLabel(plan)}</span>
                  <span className="buy-tier-vat">{plan.taxInclusive ? "including VAT" : "plus VAT"}</span>
                </button>
              );
            })}
          </div>
        </section>
      )}

      <section className="buy-checkout">
        <DiscountCodeField
          planId={selected?.id ?? null}
          period={selected?.period ?? "Monthly"}
          fullPricePence={selected?.amountPence}
          disabled={busy}
          onChange={setDiscountCode}
        />

        {error && <p className="buy-error">{error}</p>}

        <button
          className="buy-go"
          type="button"
          disabled={busy || !selected || !me?.organisationId}
          onClick={subscribe}
        >
          {busy ? "Taking you to checkout…" : copy.subscribeLabel}
        </button>

        <p className="buy-muted">
          You'll be signing in as <strong>{me?.email}</strong>. Card details are taken by Stripe and never
          touch this app.
        </p>

        <button className="trial-signout" type="button" disabled={download.busy} onClick={() => void downloadMyData()}>
          {download.busy ? "Preparing your download…" : "Download my data"}
        </button>
        {download.note && <p className="buy-muted">{download.note}</p>}

        <button className="trial-signout" type="button" onClick={() => void logout()}>
          Sign out
        </button>
      </section>
    </main>
  );
}
